import { ref, watch } from 'vue';

/**
 * The reusable track card renders at three densities:
 *  - `compact`  — one-line rows (position, cover, title/remix + artist/album, duration)
 *  - `default`  — a card with the identity above plus rating / genre / tags
 *  - `full`     — everything, including code, playlist count, ©year, bitrate / ISRC / dates
 *
 * The choice is a single page-level preference (module-level ref) persisted to
 * localStorage, so switching density in one Tracks tab updates every open track
 * list at once and the choice survives navigation.
 */
export type TrackCardDensity = 'compact' | 'default' | 'full';

export const TRACK_CARD_DENSITIES: TrackCardDensity[] = ['compact', 'default', 'full'];

const STORAGE_KEY = 'track-card-density:v1';
const DEFAULT_DENSITY: TrackCardDensity = 'default';

function readPersistedDensity(): TrackCardDensity {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return TRACK_CARD_DENSITIES.includes(stored as TrackCardDensity)
            ? (stored as TrackCardDensity)
            : DEFAULT_DENSITY;
    } catch {
        return DEFAULT_DENSITY;
    }
}

const density = ref<TrackCardDensity>(readPersistedDensity());

watch(density, (value) => {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // Private mode / no storage — keep the in-memory choice, just don't persist.
    }
});

/** Shared, persisted track-card density preference. */
export function useTrackCardDensity() {
    return density;
}
